/**
 * Photos the post-capture Filter page ran out of time for.
 *
 * view/Filter filters every captured photo when the guest presses Next, under an overall
 * deadline of max(20 s, 4 s per photo). A slow booth hits that deadline and, before this,
 * the remaining photos went into the strip, the GIF and the gallery unfiltered with nothing
 * to tell the guest or the operator (Mineroom X ROWA: 25 of 88 sessions in 30 days, at a
 * median 3.1 s per photo against 0.4 s for the rest of the fleet).
 *
 * The Filter page now hands those photos to `backgroundFilterProcessor.finishLeftovers`, and
 * Select waits for them before the uploads and the strip export. Every booth that filters
 * all its photos in time gets an empty list here and nothing else changes.
 *
 * Pure and import-free so it tests without the renderer.
 */

export interface FilterablePhoto {
  id?: string
  photoUrl?: string
  originalPhotoUrl?: string
  filteredVersions?: Record<string, string>
}

export interface LeftoverPhoto {
  id: string
  photoUrl: string
}

/**
 * The photos from `attempted` that still have no version for `filterId` in `latestItems`
 * (the gallery as it is now). Each comes back with the URL to filter: the original when one
 * is recorded, so a photo is never filtered twice.
 */
export function photosMissingFilter(
  attempted: FilterablePhoto[],
  latestItems: FilterablePhoto[],
  filterId: string
): LeftoverPhoto[] {
  const missing: LeftoverPhoto[] = []
  for (const photo of attempted) {
    if (!photo.id || !photo.photoUrl) continue
    const latest = latestItems.find((item) => item.id === photo.id)
    // Gone from the gallery (a reset or a retake replaced it): nothing left to finish.
    if (!latest) continue
    if (latest.filteredVersions?.[filterId]) continue
    missing.push({
      id: photo.id,
      photoUrl: latest.originalPhotoUrl || photo.originalPhotoUrl || photo.photoUrl
    })
  }
  return missing
}

/**
 * The filtered URLs the strip preview has to be showing before Select exports it, for the
 * photos the guest selected. Selected entries are gallery `photoUrl`s, possibly carrying the
 * `mirror:` prefix; a photo with no version for `filterId` yet contributes nothing.
 */
export function filteredUrlsForSelection(
  selected: string[],
  items: FilterablePhoto[],
  filterId: string
): string[] {
  const urls: string[] = []
  for (const entry of selected) {
    const url = entry.startsWith('mirror:') ? entry.slice(7) : entry
    if (!url) continue
    const item = items.find((i) => i.photoUrl === url || i.originalPhotoUrl === url)
    const filtered = item?.filteredVersions?.[filterId]
    if (filtered) urls.push(filtered)
  }
  return urls
}
